// Use a versioned key so installs that previously persisted the old light
// default start with the new system-controlled default once.
export const COLOR_MODE_STORAGE_KEY = "relibuild-color-mode-v2";

export type ColorModePreference = "light" | "dark" | "auto";
export type ResolvedColorMode = "light" | "dark";

export const isColorModePreference = (value: unknown): value is ColorModePreference =>
  value === "light" || value === "dark" || value === "auto";

export const getStoredColorMode = (): ColorModePreference => {
  if (typeof window === "undefined") return "auto";

  try {
    const storedMode = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    return isColorModePreference(storedMode) ? storedMode : "auto";
  } catch {
    return "auto";
  }
};

export const resolveColorMode = (preference: ColorModePreference): ResolvedColorMode => {
  if (preference !== "auto") return preference;

  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

export const applyColorMode = (preference: ColorModePreference) => {
  if (typeof document === "undefined") return;

  const resolvedMode = resolveColorMode(preference);
  const root = document.documentElement;

  root.setAttribute("data-bs-theme", resolvedMode);
  root.setAttribute("data-color-mode", preference);
  root.classList.toggle("dark", resolvedMode === "dark");
  root.style.colorScheme = resolvedMode;
  document.body?.setAttribute("data-layout-mode", resolvedMode);

  const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  themeColor?.setAttribute("content", resolvedMode === "dark" ? "#111827" : "#ffffff");
};

export const storeColorMode = (preference: ColorModePreference) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, preference);
  } catch {
    // The visual preference still works when storage is unavailable.
  }
};
